import React from "react";
import { type LucideIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { Skeleton } from "src/shadcn/ui/skeleton";
import { Card, CardContent } from "src/shadcn/ui/card";
import { cn } from "src/lib/utils";

// ---------------------------------------------------------------------------
// Variant definitions
// ---------------------------------------------------------------------------

const statCardVariants = cva("", {
  variants: {
    variant: {
      /**
       * Accent — left-border colour strip, icon floated right.
       * Used by: Pallets, Shipments, Relabelling pages.
       */
      accent:
        "relative overflow-hidden bg-white border border-slate-200/60 shadow-md hover:shadow-lg transition-shadow duration-200",
      /**
       * Flat — standard card border, icon floated left beside the label.
       * Used by: Claims page.
       */
      flat: "bg-white border border-secondary-200 rounded-xl shadow-soft p-6"
    }
  },
  defaultVariants: { variant: "accent" }
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StatCardProps extends VariantProps<typeof statCardVariants> {
  icon: LucideIcon;
  title: string;
  value: string | number;
  /** Tailwind class for the accent bar (accent variant only). e.g. "bg-emerald-500" */
  accentClass?: string;
  /** Tailwind class for the icon wrapper background. e.g. "bg-emerald-50" */
  iconBgClass: string;
  /** Tailwind class for the icon colour. e.g. "text-emerald-600" */
  iconColorClass: string;
}

export interface StatCardSkeletonProps extends VariantProps<typeof statCardVariants> {
  iconWidth?: string;
  titleWidth?: string;
  valueWidth?: string;
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

export const StatCard: React.FC<StatCardProps> = ({
  variant = "accent",
  icon: Icon,
  title,
  value,
  accentClass,
  iconBgClass,
  iconColorClass
}) => {
  if (variant === "flat") {
    return (
      <div className={statCardVariants({ variant })}>
        <div className="flex items-center gap-3 mb-4">
          <div className={cn("rounded-lg p-2", iconBgClass)}>
            <Icon className={cn("h-5 w-5", iconColorClass)} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">{title}</h3>
            <p className="text-2xl font-bold text-secondary-900 mt-1">{value}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={statCardVariants({ variant })}>
      <div className={cn("absolute inset-y-0 left-0 w-1", accentClass)} />
      <CardContent className="pl-6 pr-5 py-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground mt-0.5 tabular-nums">{value}</p>
          </div>
          <div className={cn("rounded-xl p-3", iconBgClass)}>
            <Icon className={cn("h-5 w-5", iconColorClass)} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// StatCardSkeleton
// ---------------------------------------------------------------------------

export const StatCardSkeleton: React.FC<StatCardSkeletonProps> = ({
  variant = "accent",
  iconWidth = "w-9",
  titleWidth = "w-32",
  valueWidth = "w-24"
}) => {
  if (variant === "flat") {
    return (
      <div className="bg-white border border-secondary-200 rounded-xl shadow-soft p-6">
        <div className="flex items-center gap-3 mb-4">
          <Skeleton className={cn("h-9 rounded-lg bg-secondary-300", iconWidth)} />
          <div className="flex-1">
            <Skeleton className={cn("h-4 mb-2 bg-secondary-300", titleWidth)} />
            <Skeleton className={cn("h-8 bg-secondary-300", valueWidth)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="relative overflow-hidden bg-white border border-slate-200/60 shadow-md">
      <div className="absolute inset-y-0 left-0 w-1 bg-secondary-200" />
      <CardContent className="pl-6 pr-5 py-5">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-3 w-28 bg-secondary-300" />
            <Skeleton className="h-9 w-14 mt-2 bg-secondary-300" />
          </div>
          <Skeleton className="h-11 w-11 rounded-xl bg-secondary-300" />
        </div>
      </CardContent>
    </Card>
  );
};
